import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { Col, FormLabel, Row } from "react-bootstrap";

import ListPage from "../../../layouts/page/ListPage";
import PageTitle from "../../../layouts/PageTitle";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import ListMetricCard, { ListMetricCards } from "../../../components/Common/ListParts/ListMetricCard";
import { ApiError } from "../../../components/Common/ApiError";
import { Columns } from "./Columns";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";

/** Primeiro dia do mês corrente: início padrão do período consultado. */
const startOfMonth = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
};

const AdvanceList = () => {

    const { t } = useTranslation();
    const [employeeFilter, setEmployeeFilter] = useState(null);
    // A tela abre no mês corrente, do dia 1 até hoje: é o recorte que o gestor usa para
    // fechar o acerto, e sem período a grade traria o histórico inteiro logo na abertura.
    const [startDateFilter, setStartDateFilter] = useState(startOfMonth);
    const [endDateFilter, setEndDateFilter] = useState(() => new Date());

    /** Traduz os campos da tela para os filtros que a API entende. */
    const buildFilters = useCallback((employee, startDate, endDate) => {
        const activeFilters = [];

        if (employee) {
            activeFilters.push({
                field: "employee.id",
                value: employee.value,
                operation: SearchOperation.EQUAL
            });
        }

        // Período pela data do vale, e não pela data de cadastro: é por ela que o gestor
        // fecha o mês de cada colaborador.
        if (startDate) {
            activeFilters.push({
                field: "advanceDate",
                value: formatDateToYYYYMMDD(startDate),
                operation: SearchOperation.GREATER_THAN_EQUAL
            });
        }

        if (endDate) {
            activeFilters.push({
                field: "advanceDate",
                value: formatDateToYYYYMMDD(endDate),
                operation: SearchOperation.LESS_THAN_EQUAL
            });
        }

        return activeFilters;
    }, []);

    // Filtros já aplicados na abertura, para a grade e os cards nascerem no mês corrente.
    const [filters, setFilters] = useState(() => buildFilters(null, startOfMonth(), new Date()));

    const [totalValue, setTotalValue] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [totalEmployees, setTotalEmployees] = useState(0);
    const [loadingTotals, setLoadingTotals] = useState(false);

    // Os cards somam os mesmos filtros da grade numa consulta própria: o ListPage pagina e
    // não devolve os registros para a tela, então somar o que está na página daria um total
    // menor que o real assim que passasse de uma página.
    useEffect(() => {
        setLoadingTotals(true);
        axiosInstance.post(`${Api.cash_flow.advance}/findByFilters`, { filters })
            .then(({ data }) => {
                const rows = data || [];
                setTotalValue(rows.reduce((total, item) => total + Number(item.value || 0), 0));
                setTotalCount(rows.length);
                setTotalEmployees(new Set(rows.map(item => item.employee?.id).filter(Boolean)).size);
            })
            .catch(error => ApiError(error))
            .finally(() => setLoadingTotals(false));
    }, [filters]);

    const handleSearch = () => {
        setFilters(buildFilters(employeeFilter, startDateFilter, endDateFilter));
    };

    // Limpar devolve o mês corrente, e não a tela sem período nenhum: voltar para o
    // histórico inteiro não é o que o gestor quer ao desfazer um filtro de colaborador.
    const handleClearFilters = () => {
        const start = startOfMonth();
        const end = new Date();

        setEmployeeFilter(null);
        setStartDateFilter(start);
        setEndDateFilter(end);
        setFilters(buildFilters(null, start, end));
    };

    return (
        <>
            <PageTitle activeMenu={t('cash_flow.advances')} motherMenu={t('cash_flow.label')} />

            <FilterAccordion
                title="filters"
                onSearch={handleSearch}
                onClear={handleClearFilters}
                centerButtons={false}
            >
                {/* Colunas md para os filtros empilharem no celular, que é de onde boa parte
                    da operação abre esta tela. */}
                <Row className="g-2 align-items-end">
                    <Col lg={4} md={6}>
                        <FormLabel>{t('employee')}</FormLabel>
                        <EmployeeSelect
                            filterProvidesService={false}
                            value={employeeFilter}
                            onChange={setEmployeeFilter}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('start_date')}</FormLabel>
                        <InputDate
                            selected={startDateFilter}
                            onChange={setStartDateFilter}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('end_date')}</FormLabel>
                        <InputDate
                            selected={endDateFilter}
                            onChange={setEndDateFilter}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            {/* Mantém os resumos no mesmo bloco visual dos cards de Movimentação, sem
                alterar o recorte: todos continuam refletindo exatamente os filtros aplicados. */}
            <ListMetricCards>
                <ListMetricCard
                    accent="primary"
                    label={t('cash_flow.advances_total_in_period')}
                    value={`${t('currency_symbol')} ${convertDoubleToStringPTBR(totalValue)}`}
                    icon="fas fa-hand-holding-dollar"
                    loading={loadingTotals}
                />
                <ListMetricCard
                    accent="warning"
                    label={t('cash_flow.advances_count_in_period')}
                    value={totalCount}
                    icon="fas fa-receipt"
                    loading={loadingTotals}
                />
                <ListMetricCard
                    accent="info"
                    label={t('cash_flow.advances_employees_in_period')}
                    value={totalEmployees}
                    icon="fas fa-users"
                    loading={loadingTotals}
                />
            </ListMetricCards>

            <ListPage
                showPageTitle={false}
                activeMenu={t('cash_flow.advances')}
                motherMenu={t('cash_flow.label')}
                path={Api.cash_flow.advance}
                route={MenuConfig.cash_flow.advance.route}
                columns={Columns}
                filters={filters}
            />
        </>
    );
}

export default AdvanceList;
